import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine } from "recharts";
import { useFinance } from "@/context/FinanceContext";
import { parseISO, format, addDays, addMonths, startOfMonth, endOfMonth, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { TrendingUp } from "lucide-react";
import { currencyFormatter, currencyFormatterCompact } from "@/lib/formatters";
import { Checkbox } from "@/components/ui/checkbox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

type ForecastRange = "0" | "1" | "2";

export function ForecastChart() {
  const { transactions, accounts } = useFinance();
  const [forecastRange, setForecastRange] = useState<ForecastRange>("0");
  
  const [visibleLines, setVisibleLines] = useState({
    saldo: true,
    entradas: true,
    saidas: true
  });

  const toggleLine = (line: keyof typeof visibleLines) => {
    setVisibleLines(prev => ({ ...prev, [line]: !prev[line] }));
  };

  const extraMonths = parseInt(forecastRange);

  // Pre-compute transaction index and current balance (shared across memos)
  const { currentBalance, transactionsByDate } = useMemo(() => {
    const balance = accounts.reduce((sum, acc) => sum + (acc.initialBalance || 0), 0) +
      transactions
        .filter(t => t.settled)
        .reduce((sum, t) => sum + (t.type === "receita" ? t.value : -t.value), 0);

    const byDate = new Map<string, { revenue: number; expenses: number }>();
    transactions.forEach(t => {
      if (t.settled) return;
      if (!t.dueDate) return;
      const dateStr = format(parseISO(t.dueDate), "yyyy-MM-dd");
      const existing = byDate.get(dateStr) || { revenue: 0, expenses: 0 };
      if (t.type === "receita") {
        existing.revenue += t.value;
      } else {
        existing.expenses += t.value;
      }
      byDate.set(dateStr, existing);
    });

    return { currentBalance: balance, transactionsByDate: byDate };
  }, [transactions, accounts]);

  // Compute end-of-month balances for each of the 3 months
  const endOfMonthBalances = useMemo(() => {
    const today = new Date();
    const results: number[] = [];
    for (let m = 0; m <= 2; m++) {
      const monthEnd = endOfMonth(addMonths(today, m));
      const days = differenceInDays(monthEnd, today);
      let balance = currentBalance;
      for (let i = 1; i <= days; i++) {
        const d = addDays(today, i);
        const dateStr = format(d, "yyyy-MM-dd");
        const dayData = transactionsByDate.get(dateStr);
        if (dayData) {
          balance += dayData.revenue - dayData.expenses;
        }
      }
      results.push(balance);
    }
    return results;
  }, [currentBalance, transactionsByDate]);

  const forecastData = useMemo(() => {
    const today = new Date();
    const monthStart = startOfMonth(today);
    const endDate = endOfMonth(addMonths(today, extraMonths));
    const forecastDays = differenceInDays(endDate, today);
    
    const daysBeforeToday = differenceInDays(today, monthStart);

    const chartData = [];
    let runningBalance = currentBalance;

    for (let i = -daysBeforeToday; i <= forecastDays; i++) {
      const forecastDate = addDays(today, i);
      const dateStr = format(forecastDate, "yyyy-MM-dd");
      const dayData = transactionsByDate.get(dateStr) || { revenue: 0, expenses: 0 };

      if (i <= 0) {
        chartData.push({
          date: format(forecastDate, "dd/MM", { locale: ptBR }),
          fullDate: forecastDate,
          entradas: i === 0 ? dayData.revenue : 0,
          saidas: i === 0 ? dayData.expenses : 0,
          saldo: currentBalance,
          isToday: i === 0,
          isPast: i < 0,
          hasTransaction: i === 0 ? (dayData.revenue > 0 || dayData.expenses > 0) : false
        });
      } else {
        runningBalance += dayData.revenue - dayData.expenses;
        chartData.push({
          date: format(forecastDate, "dd/MM", { locale: ptBR }),
          fullDate: forecastDate,
          entradas: dayData.revenue,
          saidas: dayData.expenses,
          saldo: runningBalance,
          isToday: false,
          isPast: false,
          hasTransaction: dayData.revenue > 0 || dayData.expenses > 0
        });
      }
    }

    const firstDate = chartData[0]?.date;
    const lastDate = chartData[chartData.length - 1]?.date;
    const todayDate = chartData.find(d => d.isToday)?.date;
    const transactionDates = chartData
      .filter(d => d.hasTransaction && !d.isToday)
      .map(d => d.date);
    
    const tickSet = new Set([firstDate, todayDate, ...transactionDates, lastDate].filter(Boolean));
    const allTicks = Array.from(tickSet);

    return {
      chartData,
      currentBalance,
      maxValue: Math.max(...chartData.map(d => Math.max(d.entradas, d.saidas, d.saldo))),
      minValue: Math.min(...chartData.map(d => Math.min(d.saldo, 0))),
      ticksWithTransactions: allTicks
    };
  }, [currentBalance, transactionsByDate, extraMonths]);

  const chartConfig = {
    saldo: { label: "Saldo", color: "hsl(var(--primary))" },
    entradas: { label: "Entradas", color: "#10b981" },
    saidas: { label: "Saídas", color: "#ef4444" }
  };

  // Formata valor compacto sem "R$"
  const formatCompact = (value: number) => {
    const abs = Math.abs(value);
    if (abs >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return value.toFixed(0);
  };

  // Custom label renderer with smart positioning to avoid overlaps
  const renderLabel = (dataKey: string, color: string, position: "above" | "below" | "auto") => (props: any) => {
    const { x, y, value, index } = props;
    if (!value || value === 0) return null;
    const point = forecastData.chartData[index];
    if (!point?.hasTransaction && !point?.isToday && index > 0) return null;

    let offsetY: number;
    if (position === "above") {
      offsetY = -14;
    } else if (position === "below") {
      offsetY = 20;
    } else {
      // auto: saldo acima, entradas/saídas alternam
      offsetY = dataKey === "saldo" ? -14 : 20;
    }
    
    return (
      <text
        x={x}
        y={y + offsetY}
        fill={color}
        fontSize={11}
        fontWeight={700}
        textAnchor="middle"
      >
        {formatCompact(value)}
      </text>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <TrendingUp className="h-5 w-5" />
            Previsão de entradas, saídas e saldo
          </CardTitle>
          <ToggleGroup
            type="single"
            value={forecastRange}
            onValueChange={(v) => { if (v) setForecastRange(v as ForecastRange); }}
            className="bg-muted rounded-lg p-0.5"
          >
            <ToggleGroupItem value="0" className="text-xs px-3 py-1.5 h-auto flex flex-col gap-0.5 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-md">
              <span>{format(new Date(), "MMM/yy", { locale: ptBR })}</span>
              <span className="text-[10px] font-normal opacity-75">{formatCompact(endOfMonthBalances[0])}</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="1" className="text-xs px-3 py-1.5 h-auto flex flex-col gap-0.5 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-md">
              <span>até {format(addMonths(new Date(), 1), "MMM/yy", { locale: ptBR })}</span>
              <span className="text-[10px] font-normal opacity-75">{formatCompact(endOfMonthBalances[1])}</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="2" className="text-xs px-3 py-1.5 h-auto flex flex-col gap-0.5 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-md">
              <span>até {format(addMonths(new Date(), 2), "MMM/yy", { locale: ptBR })}</span>
              <span className="text-[10px] font-normal opacity-75">{formatCompact(endOfMonthBalances[2])}</span>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ChartContainer config={chartConfig} className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={forecastData.chartData}
                margin={{ left: 50, right: 20, top: 30, bottom: 55 }}
              >
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={24}
                  fontSize={13}
                  fontWeight={700}
                  tick={{ fill: "hsl(var(--foreground))" }}
                  angle={0}
                  textAnchor="middle"
                  ticks={forecastData.ticksWithTransactions}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  width={70}
                  fontSize={12}
                  fontWeight={500}
                  tick={{ fill: "#000000" }}
                  tickFormatter={(value) => {
                    if (Math.abs(value) >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
                    if (Math.abs(value) >= 1000) return `R$ ${(value / 1000).toFixed(0)}k`;
                    return `R$ ${value.toFixed(0)}`;
                  }}
                />
                <ChartTooltip
                  cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
                  content={
                    <ChartTooltipContent
                      formatter={(value, name) => [
                        currencyFormatter.format(value as number),
                        ` ${name}`,
                      ]}
                      labelFormatter={(label, payload) => {
                        if (payload && payload[0]) {
                          const data = payload[0].payload;
                          return format(data.fullDate, "dd 'de' MMMM", { locale: ptBR });
                        }
                        return label;
                      }}
                    />
                  }
                />
                
                <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" strokeOpacity={0.5} />
                
                {visibleLines.saldo && (
                  <Line
                    dataKey="saldo"
                    type="monotone"
                    stroke="var(--color-saldo)"
                    strokeWidth={3}
                    dot={(props: any) => {
                      const { cx, cy, payload, index } = props;
                      if (!payload.hasTransaction && !payload.isToday && index > 0) return null;
                      return <circle cx={cx} cy={cy} r={4} fill="var(--color-saldo)" />;
                    }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    label={renderLabel("saldo", "hsl(var(--primary))", "above")}
                  />
                )}
                
                {visibleLines.entradas && (
                  <Line
                    dataKey="entradas"
                    type="monotone"
                    stroke="var(--color-entradas)"
                    strokeWidth={2}
                    dot={(props: any) => {
                      const { cx, cy, payload } = props;
                      if (payload.entradas === 0) return null;
                      return <circle cx={cx} cy={cy} r={4} fill="var(--color-entradas)" />;
                    }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    label={renderLabel("entradas", "#10b981", "above")}
                  />
                )}
                
                {visibleLines.saidas && (
                  <Line
                    dataKey="saidas"
                    type="monotone"
                    stroke="var(--color-saidas)"
                    strokeWidth={2}
                    dot={(props: any) => {
                      const { cx, cy, payload } = props;
                      if (payload.saidas === 0) return null;
                      return <circle cx={cx} cy={cy} r={4} fill="var(--color-saidas)" />;
                    }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    label={renderLabel("saidas", "#ef4444", "below")}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>
        
        {/* Legenda com checkboxes */}
        <div className="flex justify-center gap-6 mt-4 text-sm">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={visibleLines.saldo}
              onCheckedChange={() => toggleLine('saldo')}
              className="border-primary data-[state=checked]:bg-primary"
            />
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: chartConfig.saldo.color }}></div>
            <span className={visibleLines.saldo ? "font-medium" : "text-muted-foreground"}>Saldo</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={visibleLines.entradas}
              onCheckedChange={() => toggleLine('entradas')}
              className="border-green-500 data-[state=checked]:bg-green-500"
            />
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: chartConfig.entradas.color }}></div>
            <span className={visibleLines.entradas ? "font-medium" : "text-muted-foreground"}>Entradas</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={visibleLines.saidas}
              onCheckedChange={() => toggleLine('saidas')}
              className="border-red-500 data-[state=checked]:bg-red-500"
            />
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: chartConfig.saidas.color }}></div>
            <span className={visibleLines.saidas ? "font-medium" : "text-muted-foreground"}>Saídas</span>
          </label>
        </div>
      </CardContent>
    </Card>
  );
}
